import debug from "debug";
const log = debug("app:ur");

/**
 * Class representing an action handler for managing object additions, removals, and loads.
 * @category Components
 * @subcategory UndoRedoManager
 */
export default class AddAction {
  /**
   * List of valid action types for the AddAction class.
   * @type {Array<string>}
   */
  static ACTION_TYPES = ["add", "auto", "load", "copy", "duplicate"];

  /**
   * Constructor for AddAction.
   * @param {Object} experience - The experience object containing scene and event hub references.
   * @param {Object} experience.eventHub - The event hub for emitting and listening to events.
   * @param {Object} experience.scene - The scene object for managing 3D objects.
   */
  constructor(experience) {
    /**
     * The experience object containing scene and event hub references.
     * @type {Object}
     */
    this.experience = experience;

    /**
     * The event hub for emitting and listening to events.
     * @type {Object}
     */
    this.eventHub = this.experience.eventHub;

    /**
     * The scene object for managing 3D objects.
     * @type {Object}
     */
    this.scene = this.experience.scene;
  }

  /**
   * Initializes the action based on the provided event type, action, and event ID.
   * @param {string} eventType - The type of event (e.g., "add", "load").
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {string} eventId - The unique ID of the event.
   * @returns {Promise<void>} - A promise that resolves once the action is handled.
   */
  init(eventType, action, eventId) {
    return new Promise((resolve, reject) => {
      try {
        log("Initializing AddAction:", { eventType, action, eventId });

        // Use the static ACTION_TYPES array for clarity
        if (AddAction.ACTION_TYPES.includes(eventType)) {
          this.handleAdd(eventType, action, eventId, resolve);
        } else {
          log("Unhandled eventType in AddAction:", eventType);
          resolve(); // Resolve if the event type is not relevant
        }
      } catch (error) {
        log("Error in AddAction.init:", error);
        reject(error);
      }
    });
  }

  cleanup() {
    if (!this.eventHub) return; // Ensure valid instance before cleanup

    log("Cleaning up AddAction...");

    // Unregister event listeners
    this.eventHub.off("makeParts", this.addObjects);
    this.eventHub.off("makePartsLoader", this.loadObjects);
    this.eventHub.off("deleteObjects", this.removeObjects);

    // Clear references
    this.experience = null;
    this.eventHub = null;
    this.scene = null;

    log("AddAction cleanup completed.");
  }

  /**
   * Handles adding or removing objects based on the event type and action.
   * @param {string} eventType - The type of event (e.g., "add", "load").
   * @param {string} action - The action to perform ("undo" or "redo").
   * @param {string} eventId - The unique ID of the event.
   * @param {Function} resolve - The resolve function for the promise.
   */
  handleAdd(eventType, action, eventId, resolve) {
    log("handleAdd", eventType, action, eventId);

    // Retrieve event data from the undo/redo vault
    const event = this.experience.undoRedoVault.eventLog.get(eventId);
    if (!event) {
      log("No event data found for eventId:", eventId);
      resolve();
      return;
    }

    const payload = event.data;

    if (action === "undo") {
      this.removeObjects(payload);
    } else if (action === "redo") {
      if (eventType === "load") {
        this.loadObjects(payload);
      } else {
        this.addObjects(payload);
      }
    }
    resolve();
  }

  /**
   * Emits an event to add objects based on the provided payload.
   * @param {Object} payload - The data describing the objects to add.
   */
  addObjects(payload) {
    log("makeParts event emitted:", payload);
    this.eventHub.emit("makeParts", payload);
  }

  /**
   * Emits an event to load objects based on the provided payload.
   * @param {Object} payload - The data describing the objects to load.
   */
  loadObjects(payload) {
    log("MakePartsLoader event emitted:", payload);
    this.eventHub.emit("makePartsLoader", payload);
  }

  /**
   * Emits an event to remove objects based on the provided payload.
   * @param {Object} payload - The data describing the objects to remove.
   */
  removeObjects(payload) {
    log("deleteObjects event emitted:", payload);
    this.eventHub.emit("deleteObjects", payload);
  }
}
